import React, { useEffect, useMemo, useState } from "react";
import { db, functions } from "../firebase";
import { httpsCallable } from "firebase/functions";
import {
    collection,
    doc,
    addDoc,
    setDoc,
    getDoc,
    onSnapshot,
    serverTimestamp,
    query,
    orderBy,
    limit,
    updateDoc,
} from "firebase/firestore";

export default function DeveloperPanel() {
    // --- Banner state ---
    const [banner, setBanner] = useState({
        enabled: false,
        type: "warning", // info | warning | danger | success
        message: "",
        linkText: "",
        linkUrl: "",
    });
    const [savingBanner, setSavingBanner] = useState(false);

    // --- Changelog draft state ---
    const [draft, setDraft] = useState({
        title: "",
        content: "",
    });
    const [publishing, setPublishing] = useState(false);
    const [savingDraft, setSavingDraft] = useState(false);
    const [recentEntries, setRecentEntries] = useState([]);
    const [connectingFreshBooks, setConnectingFreshBooks] = useState(false);
    const [freshBooksStatus, setFreshBooksStatus] = useState(null);
    const [freshBooksMessage, setFreshBooksMessage] = useState("");
    // --- Permission error for banner read ---
    const [permError, setPermError] = useState(null);

    // Load current banner
    useEffect(() => {
        const unsub = onSnapshot(
            doc(db, "adminSettings", "siteBanner"),
            (snap) => {
                if (snap.exists()) {
                    const data = snap.data();
                    setBanner((prev) => ({
                        enabled: !!data.enabled,
                        type: data.type || "warning",
                        message: data.message || "",
                        linkText: data.linkText || "",
                        linkUrl: data.linkUrl || "",
                    }));
                }
            },
            (err) => {
                console.error("DeveloperPanel banner read failed:", err);
                setPermError(err);
            }
        );
        return () => unsub();
    }, []);

    // Load existing draft
    useEffect(() => {
        const ref = doc(db, "adminSettings", "nextUpdate");
        let unsub = () => { };
        unsub = onSnapshot(
            ref,
            (snap) => {
                if (snap.exists()) {
                    const data = snap.data();
                    setDraft({
                        title: data.title || "",
                        content: data.content || "",
                    });
                } else {
                    setDraft({ title: "", content: "" });
                }
            },
            (err) => {
                console.error("DeveloperPanel draft read failed:", err);
                try { unsub(); } catch { }
                setPermError(err);
            }
        );
        return () => unsub();
    }, []);

    // Load recent published entries
    useEffect(() => {
        const qy = query(collection(db, "changelog"), orderBy("createdAt", "desc"), limit(5));
        let unsub = () => { };
        unsub = onSnapshot(
            qy,
            (snap) => {
                const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
                setRecentEntries(list);
            },
            (err) => {
                console.error("DeveloperPanel changelog read failed:", err);
                try { unsub(); } catch { }
                setPermError(err);
            }
        );
        return () => unsub();
    }, []);

    useEffect(() => {
        let active = true;
        const loadConnectionStatus = async () => {
            try {
                const getConnectionStatus = httpsCallable(
                    functions,
                    "getFreshBooksConnectionStatus"
                );
                const result = await getConnectionStatus({});
                if (active) setFreshBooksStatus(result?.data || null);
            } catch (error) {
                console.error("Failed to load FreshBooks connection status", error);
                if (active) {
                    setFreshBooksMessage(
                        `Could not load FreshBooks status: ${error?.message || "Unknown error"}`
                    );
                }
            }
        };
        loadConnectionStatus();
        return () => {
            active = false;
        };
    }, []);

    const bannerBg = useMemo(() => {
        const map = {
            warning: "#f59e0b",
            danger: "#ef4444",
            error: "#ef4444",
            info: "#2563eb",
            success: "#16a34a",
        };
        return map[String(banner.type || "").toLowerCase()] || "#111827";
    }, [banner.type]);

    const saveBanner = async () => {
        setSavingBanner(true);
        try {
            await setDoc(
                doc(db, "adminSettings", "siteBanner"),
                {
                    enabled: !!banner.enabled,
                    type: banner.type || "warning",
                    message: banner.message || "",
                    linkText: banner.linkText || "",
                    linkUrl: banner.linkUrl || "",
                    updatedAt: serverTimestamp(),
                },
                { merge: true }
            );
        } catch (e) {
            console.error("Failed to save banner", e);
            alert("Failed to save banner: " + (e?.message || e));
        } finally {
            setSavingBanner(false);
        }
    };

    const clearBanner = async () => {
        setBanner({ enabled: false, type: "warning", message: "", linkText: "", linkUrl: "" });
        await saveBanner();
    };

    const saveDraft = async () => {
        setSavingDraft(true);
        try {
            await setDoc(
                doc(db, "adminSettings", "nextUpdate"),
                {
                    title: draft.title || "",
                    content: draft.content || "",
                    updatedAt: serverTimestamp(),
                },
                { merge: true }
            );
        } catch (e) {
            console.error("Failed to save draft", e);
            alert("Failed to save draft: " + (e?.message || e));
        } finally {
            setSavingDraft(false);
        }
    };

    const publishDraft = async () => {
        // simple guard
        if (!draft.title?.trim() || !draft.content?.trim()) {
            alert("Please provide a title and some content before publishing.");
            return;
        }
        setPublishing(true);
        try {
            const ref = await addDoc(collection(db, "changelog"), {
                title: draft.title.trim(),
                content: draft.content.trim(), // treat as Markdown/plaintext
                createdAt: serverTimestamp(),
                status: "published",
            });
            // Optionally clear the draft after publishing
            await setDoc(
                doc(db, "adminSettings", "nextUpdate"),
                { title: "", content: "", updatedAt: serverTimestamp() },
                { merge: true }
            );
            setDraft({ title: "", content: "" });
        } catch (e) {
            console.error("Failed to publish", e);
            alert("Failed to publish: " + (e?.message || e));
        } finally {
            setPublishing(false);
        }
    };

    const connectFreshBooks = async () => {
        setConnectingFreshBooks(true);
        setFreshBooksMessage("");
        try {
            const startOAuth = httpsCallable(functions, "startFreshBooksOAuth");
            const result = await startOAuth({});
            const authorizationUrl = result?.data?.authorizationUrl;
            if (!authorizationUrl) {
                throw new Error("FreshBooks authorization URL was not returned.");
            }
            window.location.assign(authorizationUrl);
        } catch (e) {
            console.error("Failed to start FreshBooks connection", e);
            setFreshBooksMessage(
                `FreshBooks connection failed: ${e?.message || "Unknown error"}`
            );
            setConnectingFreshBooks(false);
        }
    };

    return (
        <div style={{ padding: "16px", maxWidth: 960, margin: "0 auto" }}>
            <h2 style={{ margin: "8px 0 16px" }}>Developer</h2>
            {permError?.code === "permission-denied" && (
                <div style={{ background: "#FEF2F2", color: "#991B1B", border: "1px solid #FCA5A5", padding: 12, borderRadius: 8, marginBottom: 12 }}>
                    Missing or insufficient permissions to read admin settings. Update your Firestore rules to allow admins to read/write
                    <code style={{ margin: "0 6px" }}>adminSettings/siteBanner</code> and <code>adminSettings/nextUpdate</code>, and allow public read of <code>adminSettings/siteBanner</code> if you want the banner visible to all users.
                </div>
            )}

            <section style={{ border: "1px solid #e5e7eb", borderRadius: 12, padding: 16, marginBottom: 24 }}>
                <h3 style={{ marginTop: 0, marginBottom: 12 }}>FreshBooks Connection</h3>
                <p style={{ marginTop: 0, opacity: 0.75 }}>
                    Access tokens refresh automatically. Use this only to connect FreshBooks for the first time or restore a revoked connection.
                </p>
                <p style={{ margin: "0 0 12px" }}>
                    Status:{" "}
                    <strong>
                        {freshBooksStatus?.connected
                            ? "Connected"
                            : freshBooksStatus?.reauthorizationRequired
                                ? "Reconnect required"
                                : "Not connected"}
                    </strong>
                    {freshBooksStatus?.accessTokenExpiresAt
                        ? ` · Current access token expires ${new Date(freshBooksStatus.accessTokenExpiresAt).toLocaleString()}`
                        : ""}
                </p>
                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                    <button
                        onClick={connectFreshBooks}
                        disabled={connectingFreshBooks}
                        style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid #e5e7eb", background: "#111827", color: "#fff" }}
                    >
                        {connectingFreshBooks
                            ? "Opening FreshBooks…"
                            : freshBooksStatus?.connected
                                ? "Reconnect FreshBooks"
                                : "Connect FreshBooks"}
                    </button>
                    {freshBooksMessage ? (
                        <span style={{ opacity: 0.85 }}>{freshBooksMessage}</span>
                    ) : null}
                </div>
                {freshBooksStatus?.lastRefreshError ? (
                    <p style={{ margin: "12px 0 0", color: "#991b1b" }}>
                        Last refresh error: {freshBooksStatus.lastRefreshError}
                    </p>
                ) : null}
            </section>

            {/* Banner Editor */}
            <section style={{ border: "1px solid #e5e7eb", borderRadius: 12, padding: 16, marginBottom: 24 }}>
                <h3 style={{ marginTop: 0, marginBottom: 12 }}>Site-wide Banner</h3>
                <div
                    style={{
                        background: bannerBg,
                        color: "#fff",
                        padding: "8px 12px",
                        borderRadius: 8,
                        fontWeight: 600,
                        marginBottom: 12,
                        minHeight: 36,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        textAlign: "center",
                    }}
                >
                    {banner.message ? banner.message : "Preview: your message will appear here."}
                    {banner.linkUrl ? (
                        <a
                            href={banner.linkUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ marginLeft: 12, textDecoration: "underline", color: "#fff" }}
                        >
                            {banner.linkText || "Learn more"}
                        </a>
                    ) : null}
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                    <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <input
                            type="checkbox"
                            checked={!!banner.enabled}
                            onChange={(e) => setBanner((b) => ({ ...b, enabled: e.target.checked }))}
                        />
                        Enable banner
                    </label>
                    <label>
                        Style
                        <select
                            value={banner.type}
                            onChange={(e) => setBanner((b) => ({ ...b, type: e.target.value }))}
                            style={{ width: "100%", padding: 8, borderRadius: 8, border: "1px solid #e5e7eb" }}
                        >
                            <option value="info">Info (blue)</option>
                            <option value="warning">Warning (amber)</option>
                            <option value="danger">Danger (red)</option>
                            <option value="success">Success (green)</option>
                        </select>
                    </label>
                    <label style={{ gridColumn: "1 / -1" }}>
                        Message
                        <input
                            type="text"
                            value={banner.message}
                            onChange={(e) => setBanner((b) => ({ ...b, message: e.target.value }))}
                            placeholder='e.g., "WE ARE CURRENTLY EXPERIENCING ISSUES"'
                            style={{ width: "100%", padding: 10, borderRadius: 8, border: "1px solid #e5e7eb" }}
                        />
                    </label>
                    <label>
                        Link text (optional)
                        <input
                            type="text"
                            value={banner.linkText}
                            onChange={(e) => setBanner((b) => ({ ...b, linkText: e.target.value }))}
                            placeholder="Status page"
                            style={{ width: "100%", padding: 10, borderRadius: 8, border: "1px solid #e5e7eb" }}
                        />
                    </label>
                    <label>
                        Link URL (optional)
                        <input
                            type="url"
                            value={banner.linkUrl}
                            onChange={(e) => setBanner((b) => ({ ...b, linkUrl: e.target.value }))}
                            placeholder="https://status.example.com"
                            style={{ width: "100%", padding: 10, borderRadius: 8, border: "1px solid #e5e7eb" }}
                        />
                    </label>
                </div>
                <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                    <button
                        onClick={saveBanner}
                        disabled={savingBanner}
                        style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid #e5e7eb", background: "#111827", color: "#fff" }}
                    >
                        {savingBanner ? "Saving…" : "Save Banner"}
                    </button>
                    <button
                        onClick={clearBanner}
                        disabled={savingBanner}
                        style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid #e5e7eb", background: "#fff" }}
                    >
                        Clear
                    </button>
                </div>
            </section>

            {/* Changelog Editor */}
            <section style={{ border: "1px solid #e5e7eb", borderRadius: 12, padding: 16 }}>
                <h3 style={{ marginTop: 0, marginBottom: 12 }}>Next Update Log</h3>
                <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 12 }}>
                    <label>
                        Title
                        <input
                            type="text"
                            value={draft.title}
                            onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))}
                            placeholder="e.g., September Improvements"
                            style={{ width: "100%", padding: 10, borderRadius: 8, border: "1px solid #e5e7eb" }}
                        />
                    </label>
                    <label>
                        Content (Markdown or plain text)
                        <textarea
                            value={draft.content}
                            onChange={(e) => setDraft((d) => ({ ...d, content: e.target.value }))}
                            rows={10}
                            placeholder="- Faster uploads\n- New admin dashboard widgets\n- Bug fixes"
                            style={{ width: "100%", padding: 10, borderRadius: 8, border: "1px solid #e5e7eb", fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}
                        />
                    </label>
                </div>
                <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                    <button
                        onClick={saveDraft}
                        disabled={savingDraft}
                        style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid #e5e7eb", background: "#fff" }}
                    >
                        {savingDraft ? "Saving…" : "Save Draft"}
                    </button>
                    <button
                        onClick={publishDraft}
                        disabled={publishing}
                        style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid #e5e7eb", background: "#111827", color: "#fff" }}
                    >
                        {publishing ? "Publishing…" : "Publish"}
                    </button>
                </div>

                {recentEntries.length > 0 && (
                    <div style={{ marginTop: 20 }}>
                        <h4 style={{ margin: "0 0 8px" }}>Recent Releases</h4>
                        <ul style={{ paddingLeft: 18, margin: 0 }}>
                            {recentEntries.map((e) => (
                                <li key={e.id} style={{ marginBottom: 6 }}>
                                    <strong>{e.title || "Untitled"}</strong>{" "}
                                    <span style={{ opacity: 0.7 }}>
                                        {e.createdAt?.toDate ? e.createdAt.toDate().toLocaleString() : ""}
                                    </span>
                                    <div style={{ whiteSpace: "pre-wrap", opacity: 0.9 }}>{e.content}</div>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </section>
        </div>
    );
}
